import { Close } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  TextField,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import Grid from '@mui/material/Grid';
import BarnUsersAutocomplete from '../../../../../../../components/Autocompletes/BarnUsers';
import TaskDefinitionAutocomplete from '../../../../../../../components/Autocompletes/TaskDefinition';
import HorseAccessTypeSelect from '../../../../../../../components/EnumSelects/HorseAccessTypeSelect';
import Horse from '../../../../../../../components/Horse';
import {
  addHorseAccess,
  updateHorseAccess,
} from '../../../../../../../redux/horseAccess/actions';
import { primary } from '../../../../../../../utils/brandColors';
import theme from '../../../../../../../utils/theme';

const defaultConfig = {
  canAddHorseNotes: false,
  canModifyHorseFeed: false,
  canModifyHorseLocation: false,
  canModifyHorseProfile: false,
  canModifyHorseProfilePicture: false,
  canViewHorseBillables: false,
  canAddHorseTasks: false,
  canAddHorseTasksIds: [],
}

const AddEditModal = ({
  open,
  onClose,
  horseId,
  horseAccess,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useColorScheme();

  // React state
  const [barnUserId, setBarnUserId] = React.useState('');
  const [type, setType] = React.useState('Client');
  const [accessNote, setAccessNote] = React.useState('');
  const [config, setConfig] = React.useState(defaultConfig);
  const [owner, setOwner] = React.useState(false);
  const [ownershipPercentage, setOwnershipPercentage] = React.useState(0);
  const [ownershipNotes, setOwnershipNotes] = React.useState('');
  const [savingError, setSavingError] = React.useState(null);

  // Redux state
  const isFetchingHorseAccess = useSelector((state) => state.horse.isFetchingHorseAccess);
  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const barnUsers = useSelector((state) => state.barn.barnUsers);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const isLoading = isFetchingHorseAccess;

  React.useEffect(() => {
    setBarnUserId(horseAccess
      ? horseAccess.user.id
      : '');
    setType(horseAccess?.type || 'Client');
    setAccessNote(horseAccess
      ? horseAccess.accessNote
      : '');
    setOwner(horseAccess
      ? horseAccess.owner
      : false);
    setConfig(horseAccess
      ? horseAccess.config || defaultConfig
      : defaultConfig);
    setOwnershipPercentage(horseAccess
      ? horseAccess.ownershipPercentage
      : 0);
    setOwnershipNotes(horseAccess
      ? horseAccess.ownershipNotes
      : '');
  }, [horseAccess, open]);

  const handleSave = () => {
    const isNewHorseAccess = !horseAccess || !horseAccess.id;
    if (!barnUserId) {
      return;
    }

    const request = {
      accessNote,
      startDate: (horseAccess && horseAccess.id)
        ? null
        : new Date(),
      config,
      owner,
      ownershipNotes,
      ownershipPercentage,
      type,
    };

    // Adding new horse access
    if (isNewHorseAccess) {
      dispatch(addHorseAccess(horseId, barnUserId, request)).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to give this user access to the horse. Please contact Questri support.');
        console.error(error);
      });
    } else {
      dispatch(updateHorseAccess(horseId, barnUserId, request)).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update this user access. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleEndHorseAccess = () => {
    dispatch(updateHorseAccess(horseId, barnUserId, { endDate: new Date() })).then(() => {
      onClose();
    }).catch((error) => {
      setSavingError(error.message || 'There was an error attempting end access for this horse. Please contact Questri support.');
      console.error(error);
    });
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 800,
        },
      }}
      aria-labelledby="modal-add-access-note"
      aria-describedby="modal-add-horse-access-description"
    >
      <DialogTitle
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          id="modal-add-horse-access-title"
          variant="h4"
          component="span"
          color="#fff"
        >
          {(horseAccess && horseAccess.id)
            ? 'Edit'
            : 'Add'} Horse Access {horseAccess && `(${horseAccess.user.userFullName})`}
        </Typography>
        <IconButton onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          paddingBottom: 0,
          p: 2,
        }}
      >
        {
          horseId && barnHorsesMap[horseId]
          && (
            <Box
              sx={{ my: 2 }}
            ><Horse horse={barnHorsesMap[horseId][0].horse} />
            </Box>
          )
        }
        <Grid container spacing={2}>
          <Grid size={6}>
            <HorseAccessTypeSelect value={type} onChange={(e) => { setType(e.target.value); }} />
          </Grid>
          <Grid size={6}>
            <BarnUsersAutocomplete
              label="User"
              multiple={false}
              value={barnUsers.find((bu) => bu.id === barnUserId) || null}
              onSelect={(e, newBarnUser) => setBarnUserId(newBarnUser
                ? newBarnUser.id
                : '')}
              limitTags={1}
            />
          </Grid>
          <Grid
            size={{ xs: 12 }}
          >
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={owner}
                    onChange={(e) => {
                      setOwner(e.target.checked);
                      if (!e.target.checked) {
                        setOwnershipPercentage(0);
                        setOwnershipNotes('');
                      }
                      setOwnershipPercentage(100);
                    }}
                    disabled={isLoading}
                  />
                )}
                label="Is this the owner of the horse."
              />
            </FormGroup>
            {
              owner && (
                <TextField
                  value={ownershipPercentage}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  onChange={(e) => { setOwnershipPercentage(e.target.value); }}
                  fullWidth
                  disabled={isLoading}
                  label="Ownership Percentage"
                />
              )
            }
            {
              owner && (
                <TextField
                  sx={{ mt: 2 }}
                  value={ownershipNotes}
                  onChange={(e) => { setOwnershipNotes(e.target.value); }}
                  fullWidth
                  disabled={isLoading}
                  label="Ownership Note"
                  multiline
                  rows={3}
                  helperText="Any additional information about the ownership of this horse."
                />
              )
            }
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <FormControl>
              <FormLabel id="permissions-controlled-radio-buttons-group">Questri Horse User Permissions</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canAddHorseNotes}
                      onChange={(e) => setConfig({
                        ...config,
                        canAddHorseNotes: e.target.checked,
                      })}
                      name="canAddHorseNotes"
                    />
                  )}
                  label="Can Add Horse Notes"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseFeed}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseFeed: e.target.checked,
                      })}
                      name="canModifyHorseFeed"
                    />
                  )}
                  label="Can Change Horse Feed"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseLocation}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseLocation: e.target.checked,
                      })}
                      name="canModifyHorseLocation"
                    />
                  )}
                  label="Can Change Horse Location"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseProfile}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseProfile: e.target.checked,
                      })}
                      name="canModifyHorseProfile"
                    />
                  )}
                  label="Can Change Horse Profile Data"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseProfilePicture}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseProfilePicture: e.target.checked,
                      })}
                      name="canModifyHorseProfilePicture"
                    />
                  )}
                  label="Can Change Horse Picture"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canViewHorseBillables}
                      onChange={(e) => setConfig({
                        ...config,
                        canViewHorseBillables: e.target.checked,
                      })}
                      name="canViewHorseBillables"
                    />
                  )}
                  label="Can View Horse Billables"
                />

                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canAddHorseTasks}
                      onChange={(e) => setConfig({
                        ...config,
                        canAddHorseTasks: e.target.checked,
                        canAddHorseTasksIds: [],
                      })}
                      name="canAddHorseTasks"
                    />
                  )}
                  label="Can Add Horse Tasks"
                  helperText="This permission allows the user to add tasks to the horse's schedule and task list."
                />
              </FormGroup>
              {
                config.canAddHorseTasks && (
                  <TaskDefinitionAutocomplete
                    label="Tasks Allowed to Add"
                    helperText="You must specify which tasks this user can add to the horse's schedule and task list if you want to give them permission to add tasks. If you do not specify any tasks, they will not be able to add any task."
                    size="small"
                    multiple
                    value={config.canAddHorseTasksIds.filter((id) => (eventTemplates.find((et) => et.id === id)))
                      .map((id) => (eventTemplates.find((et) => et.id === id) || {
                        id,
                        name: 'Unknown',
                      }))}
                    eventTemplates={null} // needs to be null so component uses all templates
                    onSelect={(e, taskDefinitions) => {
                      setConfig({
                        ...config,
                        canAddHorseTasksIds: taskDefinitions.map((et) => et.id),
                      })
                    }}
                  />
                )
              }
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              value={accessNote}
              onChange={(e) => { setAccessNote(e.target.value); }}
              fullWidth
              disabled={isLoading}
              label="Note or information about this horse user"
              multiline
              rows={5}
              helperText="Any additional information about this user and access to provide context."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >

        {!horseAccess && !savingError && <Box />}
        {horseAccess?.id && <Button onClick={handleEndHorseAccess} disabled={isLoading}>End Horse Access</Button>}
        {savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}
        <Button onClick={handleSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</Button>

      </DialogActions>
    </Dialog>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string.isRequired,
  horseAccess: PropTypes.object,
};

AddEditModal.defaultProps = { horseAccess: null };

export default AddEditModal;
